<template>
  <div
    class="stripe-person-list-table"
    v-loading="loading"
  >
    <el-table
      :data="rows"
      ref="table"
      row-key="id"
      @sort-change="doChangeSort"
    >
      <!-- Name -->
      <el-table-column :label="fields.name.label" :prop="fields.firstName.name" sortable="custom">
        <template slot-scope="scope">{{ `${presenter(scope.row, 'firstName')} ${presenter(scope.row, 'lastName')}` }}</template>
      </el-table-column>

      <!-- Email -->
      <el-table-column :label="fields.email.label" :prop="fields.email.name" sortable="custom">
        <template slot-scope="scope">{{ presenter(scope.row, 'email') }}</template>
      </el-table-column>

      <!-- Role -->
      <el-table-column :label="fields.role.label" :prop="fields.role.name">
        <template slot-scope="scope">{{ getRole(scope.row) }}</template>
      </el-table-column>

      <!-- Phone -->
      <el-table-column :label="fields.phone.label" :prop="fields.phone.name">
        <template slot-scope="scope">{{ presenter(scope.row, 'phone') }}</template>
      </el-table-column>

      <!-- Id Verificacion Status -->
      <el-table-column :label="fields.verification.label" :prop="fields.verification.name" width="180">
        <template slot-scope="scope">
          <app-status
            class="stripe-person-list-table__status"
            v-bind="requirementsConfig(scope.row)"
          />
        </template>
      </el-table-column>

      <!-- Actions -->
      <el-table-column  width="60">
        <template slot-scope="scope">
          <div class="table-actions">
            <el-button @click="handleClickEdit(scope.row)" circle icon="icon-pen" />
          </div>
        </template>
      </el-table-column>
    </el-table>

    <div class="el-pagination-wrapper">
      <el-pagination
        :current-page="pagination.currentPage || 1"
        :disabled="loading"
        :layout="paginationLayout"
        :total="count"
        @current-change="doChangePaginationCurrentPage"
        @size-change="doChangePaginationPageSize"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { StripePersonModel } from '@modules/stripe/stripe-person/stripe-person-model';
import { StripePersonPermissions } from '@modules/stripe/stripe-person/stripe-person-permissions';
import { i18n } from '@shared/i18n';
import { get } from 'lodash';
import TagsFactory  from '@shared/factories/tags-factory';

export default {
  name: 'app-stripe-person-list-table',

  props: {
    propertyId: {
      type: String,
      required: true
    },
    connectedAccountId: {
      type: String,
      required: true
    },
  },

  computed: {
    ...mapGetters({
      rows: 'stripePerson/list/rows',
      count: 'stripePerson/list/count',
      loading: 'stripePerson/list/loading',
      pagination: 'stripePerson/list/pagination',
      isMobile: 'layout/isMobile',
      currentUser: 'auth/currentUser',
      paginationLayout: 'layout/paginationLayout',
    }),

    hasPermissionToEdit() {
      return new StripePersonPermissions(this.currentUser).edit;
    },

    hasPermissionToDestroy() {
      return new StripePersonPermissions(this.currentUser).destroy;
    },
    
    fields() {
      return StripePersonModel.fields({ countryCode: '', maxOwnershipPercentage: '' });
    },

    requirementsConfig() {
      return person => {
        const { requirements: requirementsHash } = person

        let tooltipText = ``
        let color = this.getTagStyle('verificationStatus', get(person, 'verification.status', '')).color
        let text = this.getStatusLabel(get(person, 'verification.status', ''))

        if (requirementsHash) {
          const dueRequirements = [...(new Set([
            ...(requirementsHash.pastDue || []),
            ...(requirementsHash.currentlyDue || []),
            ...(requirementsHash.eventuallyDue || []),
          ]))];

          const hasDueRequirements = !!dueRequirements && !!dueRequirements.length

          if (hasDueRequirements) {
            tooltipText += `<strong>${i18n('subpropertySettings.finantial.accounts.requirements.titles.dueRequirements')}</strong>`
            tooltipText += '<ul>'
            for (const requirement of dueRequirements || []) {
              const i18nCode = `subpropertySettings.finantial.accounts.requirements.${requirement}`
              const caption = i18n(i18nCode)
              tooltipText += `<li>${caption}</li>`
            }
            tooltipText += '</ul>'
          }

          const pendingVerificationRequirements = (requirementsHash.pendingVerification || []).filter(r => !r.includes('person_'))
          const hasPendingVerificationRequirements = !!pendingVerificationRequirements && !!pendingVerificationRequirements.length

          if (hasPendingVerificationRequirements) {
            tooltipText += `<strong>${i18n('subpropertySettings.finantial.accounts.requirements.titles.currentlyVerifying')}</strong>`
            tooltipText += '<ul>'
            for (const requirement of pendingVerificationRequirements || []) {
              const i18nCode = `subpropertySettings.finantial.accounts.requirements.${requirement}`
              const caption = i18n(i18nCode)
              tooltipText += `<li>${caption}</li>`
            }
            tooltipText += '</ul>'
          }

          if (hasDueRequirements) {
            color = this.getTagStyle('verificationStatus', 'incomplete').color
            text = this.getStatusLabel('incomplete')
          } else if (hasPendingVerificationRequirements) {
            color = this.getTagStyle('verificationStatus', 'pending').color
            text = this.getStatusLabel('pending')
          }
        }

        const enabled = !tooltipText.length

        return { enabled, tooltipText, color, text }
      }
    },
  },
  methods: {
    ...mapActions({
      doChangeSort: 'stripePerson/list/doChangeSort',
      doChangePaginationCurrentPage: 'stripePerson/list/doChangePaginationCurrentPage',
      doChangePaginationPageSize: 'stripePerson/list/doChangePaginationPageSize',
      doMountTable: 'stripePerson/list/doMountTable',
      doDestroy: 'stripePerson/destroy/doDestroy',
    }),

    presenter(row, fieldName) {
      return StripePersonModel.presenter(row, fieldName);
    },

    async doDestroyWithConfirm(id) {
      try {
        await this.$confirm(
          i18n('common.areYouSure'),
          i18n('common.confirm'),
          {
            confirmButtonText: i18n('common.yes'),
            cancelButtonText: i18n('common.no'),
            type: 'warning',
          },
        );

        return this.doDestroy(id);
      } catch (error) {
        // no
      }
    },
    
    getTagStyle(keyEnum, valueEnum) {
      return new TagsFactory({ entityName: 'stripePerson' }).getTagStyle({ keyEnum, valueEnum });
    },

    getRole(row){
      const { accountOpener, director, executive, owner } = row.relationship;
      const roles = []

      if (accountOpener) roles.push('accountOpener');
      if (director) roles.push('director');
      if (executive) roles.push('executive');
      if (owner) roles.push('owner');

      return roles.map(r => i18n(`entities.stripePerson.enumerators.personType.${r}`)).join(', ');
    },

    getStatusLabel(status) {
      return i18n(`enumerators.stripePersonVerification.${status}`);
    },

    get(source, path, defaultValue) {
      return get(source, path, defaultValue);
    },

    handleClickEdit(row) {
      this.$router.push({
        name: 'stripePersonEdit',
        params: {
          connectedAccountId: this.connectedAccountId,
          id: row.id
        }  
      });
    },
  },
};
</script>

<style lang="scss">
.stripe-person-list-table {
  tr {
    cursor: default;
  }

  &__status {
    font-size: 12px;
  }
}
</style>
